import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getInboxThreads,
  getInboxMessages,
  sendInboxMessage,
  markInboxRead,
  composeInboxMail,
  resolveInboxRecipient,
  deleteInboxMessage,
  moveInboxThreadToFolder,
  reorderInboxFolder,
  deleteInboxThread,
  emptyInboxTrash,
  getInboxFolders,
  createInboxFolder,
  deleteInboxFolder,
} from '../services/inboxService';
import { lookupUsers } from '../services/userService';
import { uploadFile, downloadFile, openFile } from '../services/fileService';
import MessageCompose from '../components/MessageCompose';
import './InboxPage.css';

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const DEFAULT_INBOX_FOLDERS = [
  { key: 'all-mail', label: 'All Mail', isSystem: true },
  { key: 'inbox', label: 'Inbox', isSystem: true },
  { key: 'sent', label: 'Sent', isSystem: true },
  { key: 'archive', label: 'Archive', isSystem: true },
  { key: 'follow-up', label: 'Follow Up', isSystem: true },
  { key: 'later', label: 'Later', isSystem: true },
  { key: 'trash', label: 'Recently Deleted', isSystem: true },
];

const PRIMARY_FOLDERS = ['inbox', 'sent', 'trash'];

export default function InboxPage() {
  const { user } = useAuth();
  const location = useLocation();
  const inboxPageRef = useRef(null);
  const composeFileRef = useRef(null);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeToQuery, setComposeToQuery] = useState('');
  const [composeMatches, setComposeMatches] = useState([]);
  const [composeRecipient, setComposeRecipient] = useState(null);
  const [composeResolution, setComposeResolution] = useState(null);
  const [resolvingRecipient, setResolvingRecipient] = useState(false);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeFiles, setComposeFiles] = useState([]);
  const [composeSending, setComposeSending] = useState(false);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [folders, setFolders] = useState(DEFAULT_INBOX_FOLDERS);
  const [dragThreadId, setDragThreadId] = useState('');
  const [threadPaneWidth, setThreadPaneWidth] = useState(300);
  const [isResizingPane, setIsResizingPane] = useState(false);
  const [showFolders, setShowFolders] = useState(true);
  const [folderSearchQuery, setFolderSearchQuery] = useState('');
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 1024;
  });
  const [mobilePane, setMobilePane] = useState('list');

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobileViewport(mobile);
      if (!mobile) setMobilePane('list');
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInboxThreads({ folder: activeFolder });
      setThreads(res.data.threads || []);
    } catch (err) {
      console.error('Failed to load inbox:', err);
    } finally {
      setLoading(false);
    }
  }, [activeFolder]);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  useEffect(() => {
    const activeId = location.state?.activeThreadId;
    if (!activeId || threads.length === 0) return;
    const found = threads.find((thread) => String(thread._id) === String(activeId));
    if (found) openThread(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, location.state?.activeThreadId]);

  useEffect(() => {
    let cancelled = false;
    getInboxFolders()
      .then((res) => {
        if (cancelled) return;
        const next = Array.isArray(res?.data?.folders) && res.data.folders.length > 0
          ? res.data.folders
          : DEFAULT_INBOX_FOLDERS;
        setFolders(next);
      })
      .catch(() => {
        if (!cancelled) setFolders(DEFAULT_INBOX_FOLDERS);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setActiveThread(null);
    setMessages([]);
    if (isMobileViewport) setMobilePane('list');
  }, [activeFolder, isMobileViewport]);

  useEffect(() => {
    if (!isResizingPane || isMobileViewport) return undefined;

    const onMouseMove = (event) => {
      const container = inboxPageRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const nextWidth = event.clientX - rect.left;
      const minWidth = 240;
      const maxWidth = Math.max(360, rect.width - 360);
      const clamped = Math.min(maxWidth, Math.max(minWidth, nextWidth));
      setThreadPaneWidth(clamped);
    };

    const onMouseUp = () => setIsResizingPane(false);

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isResizingPane, isMobileViewport]);

  const openThread = async (thread) => {
    setComposeOpen(false);
    setActiveThread(thread);
    setMessages([]);
    setMsgLoading(true);
    try {
      const res = await getInboxMessages(thread._id);
      setMessages(res.data.messages || []);
      if (isMobileViewport) setMobilePane('detail');
      // Optimistically clear unread badge
      setThreads((prev) =>
        prev.map((t) => t._id === thread._id ? { ...t, unreadCount: 0 } : t)
      );
      markInboxRead(thread._id).catch(() => {});
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setMsgLoading(false);
    }
  };

  // Called by MessageCompose — must return a promise (throw on error)
  const handleMessageSend = async ({ body, files }) => {
    const uploadedAttachments = [];
    const spaceId = activeThread?.spaceId?._id || activeThread?.spaceId || null;

    for (const file of files || []) {
      const fileRes = await uploadFile(spaceId, file);
      uploadedAttachments.push({
        fileRef: fileRes.data.file._id,
        name: fileRes.data.file.originalName || file.name,
      });
    }

    const res = await sendInboxMessage(activeThread._id, { body, attachments: uploadedAttachments });
    setMessages((prev) => [...prev, res.data.message]);
    setThreads((prev) =>
      prev.map((t) =>
        t._id === activeThread._id ? { ...t, lastMessageAt: new Date().toISOString() } : t
      )
    );
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await deleteInboxMessage(messageId);
      setMessages((prev) => prev.filter((m) => m._id !== messageId));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete message');
    }
  };

  const handleMoveThreadToFolder = async (threadId, folder) => {
    if (folder === 'all-mail') return;
    try {
      await moveInboxThreadToFolder(threadId, folder);
      setThreads((prev) => prev.filter((thread) => thread._id !== threadId));
      if (activeThread?._id === threadId) {
        setActiveThread(null);
        setMessages([]);
      }
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to move thread');
    }
  };

  const handleDropOnThread = async (targetId) => {
    if (activeFolder === 'all-mail') return;
    if (!dragThreadId || dragThreadId === targetId) return;

    const draggedIndex = threads.findIndex((thread) => thread._id === dragThreadId);
    const targetIndex = threads.findIndex((thread) => thread._id === targetId);
    if (draggedIndex === -1 || targetIndex === -1) return;

    const reordered = [...threads];
    const [dragged] = reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, dragged);
    setThreads(reordered);

    try {
      await reorderInboxFolder(activeFolder, reordered.map((thread) => thread._id));
    } catch (err) {
      fetchInbox();
      alert(err?.response?.data?.message || 'Failed to save order');
    }
  };

  const handleDeleteConversation = async () => {
    if (!activeThread?._id) return;
    if (!window.confirm('Delete this whole email chain? It will move to Recently Deleted for your account only.')) return;
    try {
      await deleteInboxThread(activeThread._id);
      setThreads((prev) => prev.filter((thread) => thread._id !== activeThread._id));
      setActiveThread(null);
      setMessages([]);
      if (isMobileViewport) setMobilePane('list');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete conversation');
    }
  };

  const handleArchiveConversation = async () => {
    if (!activeThread?._id) return;
    try {
      await moveInboxThreadToFolder(activeThread._id, 'archive');
      setThreads((prev) => prev.filter((thread) => thread._id !== activeThread._id));
      setActiveThread(null);
      setMessages([]);
      if (isMobileViewport) setMobilePane('list');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to archive conversation');
    }
  };

  const handleRestoreConversation = async () => {
    if (!activeThread?._id) return;
    try {
      await moveInboxThreadToFolder(activeThread._id, 'inbox');
      setThreads((prev) => prev.filter((thread) => thread._id !== activeThread._id));
      setActiveThread(null);
      setMessages([]);
      if (isMobileViewport) setMobilePane('list');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to restore conversation');
    }
  };

  const handleAddFolder = async () => {
    const name = window.prompt('New folder name');
    if (!name || !name.trim()) return;
    try {
      const res = await createInboxFolder(name.trim());
      const next = Array.isArray(res?.data?.folders) && res.data.folders.length > 0
        ? res.data.folders
        : folders;
      setFolders(next);
      const created = res?.data?.folder;
      if (created?.key) setActiveFolder(created.key);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create folder');
    }
  };

  const handleDeleteActiveFolder = async () => {
    const target = folders.find((entry) => entry.key === activeFolder);
    if (!target || target.isSystem) return;
    if (!window.confirm(`Delete folder "${target.label}"? Emails in it will move back to Inbox.`)) return;
    try {
      const res = await deleteInboxFolder(target.key);
      const next = Array.isArray(res?.data?.folders) && res.data.folders.length > 0
        ? res.data.folders
        : DEFAULT_INBOX_FOLDERS;
      setFolders(next);
      setActiveFolder('inbox');
      setActiveThread(null);
      setMessages([]);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete folder');
    }
  };

  const handleEmptyTrash = async () => {
    if (activeFolder !== 'trash') return;
    if (!window.confirm('Empty trash? This will permanently remove trashed conversations for you.')) return;
    try {
      await emptyInboxTrash();
      setThreads([]);
      setActiveThread(null);
      setMessages([]);
      if (isMobileViewport) setMobilePane('list');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to empty trash');
    }
  };

  useEffect(() => {
    const q = composeToQuery.trim();
    if (!composeOpen || !q || composeRecipient) {
      setComposeMatches([]);
      setComposeResolution(composeRecipient ? { resolved: true, destinationType: 'user', canSend: true, user: composeRecipient } : null);
      setResolvingRecipient(false);
      return;
    }

    let ignore = false;
    setResolvingRecipient(true);
    Promise.allSettled([lookupUsers(q), resolveInboxRecipient(q)])
      .then(([lookupResult, resolveResult]) => {
        if (ignore) return;
        setComposeMatches(lookupResult.status === 'fulfilled' ? lookupResult.value.data.users || [] : []);
        setComposeResolution(resolveResult.status === 'fulfilled' ? resolveResult.value.data.resolution : null);
      })
      .catch(() => {
        if (!ignore) setComposeMatches([]);
      })
      .finally(() => {
        if (!ignore) setResolvingRecipient(false);
      });

    return () => {
      ignore = true;
    };
  }, [composeOpen, composeToQuery, composeRecipient]);

  const startCompose = () => {
    setActiveThread(null);
    setMessages([]);
    setComposeOpen(true);
    setComposeToQuery('');
    setComposeMatches([]);
    setComposeRecipient(null);
    setComposeResolution(null);
    setResolvingRecipient(false);
    setComposeSubject('');
    setComposeBody('');
    setComposeFiles([]);
    if (isMobileViewport) setMobilePane('detail');
  };

  const handleBackToList = () => {
    setComposeOpen(false);
    setActiveThread(null);
    setMessages([]);
    setMobilePane('list');
  };

  const getThreadFromTo = (thread) => {
    const myId = String(user?._id);
    const sender = thread.createdBy;
    const senderId = String(sender?._id || sender);
    const iAmSender = senderId === myId;

    // All participants except sender are recipients
    const recipients = (thread.participants || []).filter(
      (p) => String(p._id || p) !== senderId
    );

    const fromUser = iAmSender ? { name: 'Me', _id: myId } : sender;
    const fromLabel = iAmSender ? 'Me' : (sender?.name || 'Unknown');

    const toLabel = recipients.length === 0
      ? 'No recipients'
      : recipients.length === 1
        ? (recipients[0]?.name || 'Unknown')
        : `${recipients[0]?.name || 'Unknown'} +${recipients.length - 1} more`;

    return {
      iAmSender,
      from: fromUser,
      fromLabel,
      to: recipients,
      toLabel,
      allParticipants: thread.participants || [],
    };
  };

  const startComposeToUser = (targetUser, subject = '') => {
    if (!targetUser || !targetUser._id) return;
    setComposeOpen(true);
    setComposeRecipient(targetUser);
    setComposeResolution({ resolved: true, destinationType: 'user', canSend: true, user: targetUser });
    setComposeToQuery(targetUser.name || '');
    setComposeMatches([]);
    setComposeSubject(subject ? `Re: ${subject}` : '');
    setComposeBody('');
    setComposeFiles([]);
    setActiveThread(null);
    setMessages([]);
    if (isMobileViewport) setMobilePane('detail');
  };

  const getHeaderUsers = (thread) => {
    if (!thread) return { from: null, to: [] };
    const ft = getThreadFromTo(thread);
    const myId = String(user?._id);
    const from = ft.iAmSender
      ? null
      : (ft.from && String(ft.from._id || '') !== myId ? ft.from : null);
    const to = (ft.to || []).filter((p) => String(p._id || '') !== myId);
    return { from, to };
  };

  const handleComposeSend = async () => {
    const resolvedRecipient = composeRecipient || (composeResolution?.destinationType === 'user' && composeResolution?.canSend ? composeResolution.user : null);
    if (!resolvedRecipient) {
      alert(composeResolution?.reason || 'Enter an internal person or known address. External sending is unavailable until a transport is connected.');
      return;
    }
    if (!composeSubject.trim()) {
      alert('Subject is required');
      return;
    }
    if (!composeBody.trim() && composeFiles.length === 0) {
      alert('Message body or attachments are required');
      return;
    }

    setComposeSending(true);
    try {
      const uploadedAttachments = [];
      for (const file of composeFiles) {
        const fileRes = await uploadFile(null, file, null, [resolvedRecipient._id]);
        uploadedAttachments.push({
          fileRef: fileRes.data.file._id,
          name: fileRes.data.file.originalName || file.name,
        });
      }

      const res = await composeInboxMail({
        recipientId: resolvedRecipient._id,
        recipientText: composeToQuery,
        subject: composeSubject,
        body: composeBody,
        attachments: uploadedAttachments,
      });

      const createdThread = res.data.thread;
      const firstMessage = res.data.message;

      setThreads((prev) => [
        createdThread,
        ...prev.filter((thread) => thread._id !== createdThread._id),
      ]);

      setActiveThread(createdThread);
      setMessages(firstMessage ? [firstMessage] : []);
      setComposeOpen(false);
      setComposeToQuery('');
      setComposeMatches([]);
      setComposeRecipient(null);
      setComposeResolution(null);
      setComposeSubject('');
      setComposeBody('');
      setComposeFiles([]);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to send message');
    } finally {
      setComposeSending(false);
    }
  };

  return (
    <div ref={inboxPageRef} className={`inbox-page ${isMobileViewport ? `mobile-pane-${mobilePane}` : ''}`}>
      {/* Thread list */}
      <div
        className="inbox-thread-panel"
        style={isMobileViewport ? undefined : { width: `${threadPaneWidth}px` }}
      >
        <div className="inbox-panel-header">
          <span>Inbox</span>
          <div className="inbox-header-actions">
            <Link className="btn btn-ghost btn-sm" to="/mailboxes">Shared Inboxes</Link>
            <button className="btn btn-ghost btn-sm" onClick={handleAddFolder} title="Add folder">
              + Folder
            </button>
            <button className="btn btn-primary btn-sm" onClick={startCompose} title="New message">
              + New Message
            </button>
            <button className="btn-icon" onClick={fetchInbox} title="Refresh">↻</button>
          </div>
        </div>

      <div className="inbox-source-panel">
      <div className="inbox-primary-folders">
        {['inbox', 'sent', 'trash'].map((key) => {
          const folder = folders.find((f) => f.key === key);
          if (!folder) return null;
          return (
            <button
              key={folder.key}
              type="button"
              className={`inbox-primary-btn ${activeFolder === folder.key ? 'active' : ''}`}
              onClick={() => setActiveFolder(folder.key)}
            >
              {folder.label}
            </button>
          );
        })}
        {activeFolder === 'trash' && (
          <button
            type="button"
            className="inbox-primary-btn inbox-danger-btn"
            onClick={handleEmptyTrash}
            title="Permanently clear trash"
          >
            Empty Trash
          </button>
        )}
      </div>

      <div className="inbox-other-folders-section">
        <button
          type="button"
          className="inbox-other-folders-toggle"
          onClick={() => setShowFolders(!showFolders)}
        >
          {showFolders ? '▼' : '▶'} Folders
        </button>
        {showFolders && (
          <>
            <input
              type="text"
              className="inbox-folder-search"
              placeholder="Search folders..."
              value={folderSearchQuery}
              onChange={(e) => setFolderSearchQuery(e.target.value)}
            />
            <div className="inbox-other-folders-list">
              {folders
                .filter((f) => !PRIMARY_FOLDERS.includes(f.key))
                .filter((f) => f.label.toLowerCase().includes(folderSearchQuery.toLowerCase()))
                .map((folder) => (
                  <button
                    key={folder.key}
                    type="button"
                    className={`inbox-folder-item ${activeFolder === folder.key ? 'active' : ''}`}
                    onClick={() => setActiveFolder(folder.key)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (!dragThreadId) return;
                      if (folder.key === activeFolder) return;
                      if (folder.key === 'all-mail') return;
                      handleMoveThreadToFolder(dragThreadId, folder.key);
                      setDragThreadId('');
                    }}
                  >
                    <span className="inbox-folder-item-name">{folder.label}</span>
                    {!folder.isSystem && (
                      <button
                        type="button"
                        className="inbox-folder-item-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveFolder(folder.key);
                          handleDeleteActiveFolder();
                        }}
                        title="Delete folder"
                      >
                        ×
                      </button>
                    )}
                  </button>
                ))}
            </div>
          </>
        )}
      </div>
      </div>

      <ul className="inbox-thread-list">
        {!loading && threads.length === 0 && (
          <li className="inbox-no-threads">No messages</li>
        )}
        {!loading && threads.length > 0 &&
          threads.map((t) => (
            <li
              key={t._id}
              className={`inbox-thread-item ${activeThread?._id === t._id ? 'active' : ''} ${t.unreadCount > 0 ? 'unread' : ''}`}
              draggable
              onDragStart={() => setDragThreadId(t._id)}
              onDragEnd={() => setDragThreadId('')}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleDropOnThread(t._id);
                setDragThreadId('');
              }}
              onClick={() => openThread(t)}
            >
              {(() => {
                const ft = getThreadFromTo(t);
                const senderInitial = ft.iAmSender
                  ? 'Me'
                  : (ft.from?.name?.[0]?.toUpperCase() || '?');
                const senderColor = ft.iAmSender ? '#6b7280' : undefined;
                return (
                  <>
                    <div className="inbox-thread-top">
                      <span className="inbox-space-badge" style={senderColor ? { background: senderColor } : undefined}>
                        {senderInitial === 'Me' ? '→' : senderInitial}
                      </span>
                      <span className="inbox-subject">{t.subject}</span>
                      <span className="inbox-time">{timeAgo(t.lastMessageAt)}</span>
                      {t.unreadCount > 0 && (
                        <span className="inbox-unread-badge">{t.unreadCount}</span>
                      )}
                    </div>
                  </>
                );
              })()}
              <div className="inbox-thread-hint">Drag to reorder or drop on another folder tab</div>
            </li>
          ))}
      </ul>
      </div>

      {!isMobileViewport && (
        <div
          className={`inbox-splitter ${isResizingPane ? 'active' : ''}`}
          onMouseDown={() => setIsResizingPane(true)}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize inbox panels"
        />
      )}

      {/* Message panel */}
      <div className="inbox-message-panel">
        {composeOpen ? (
          <div className="inbox-compose-panel">
            {isMobileViewport && (
              <button type="button" className="btn btn-ghost btn-sm inbox-mobile-back" onClick={handleBackToList}>
                ← Inbox
              </button>
            )}
            <h2>New Message</h2>
            <div className="inbox-compose-field">
              <label>To</label>
              <input
                value={composeRecipient ? composeRecipient.name : composeToQuery}
                onChange={(e) => {
                  setComposeRecipient(null);
                  setComposeResolution(null);
                  setComposeToQuery(e.target.value);
                }}
                placeholder="Name, handle, or email address"
              />
              {composeToQuery.trim() && (
                <div className={`inbox-recipient-resolution ${composeResolution?.canSend ? 'resolved' : composeResolution?.destinationType === 'external' ? 'external' : ''}`}>
                  {resolvingRecipient ? 'Resolving destination...' : composeResolution?.destinationType === 'user' ? `Sending inside ComSapp to ${composeResolution.user?.name || composeToQuery}` : composeResolution?.destinationType === 'space' ? `${composeResolution.space?.name || 'Space'} resolved. Open the Space to send there.` : composeResolution?.destinationType === 'external' ? `External address detected: ${composeResolution.externalAddress}. External sending is unavailable until a transport is connected.` : composeResolution?.reason || 'No internal destination resolved yet.'}
                </div>
              )}
              {!composeRecipient && composeToQuery.trim() && composeMatches.length > 0 && (
                <ul className="inbox-compose-matches">
                  {composeMatches.map((user) => (
                    <li
                      key={user._id}
                      onClick={() => {
                        setComposeRecipient(user);
                        setComposeResolution({ resolved: true, destinationType: 'user', canSend: true, user });
                        setComposeToQuery(user.name);
                        setComposeMatches([]);
                      }}
                    >
                      <span>{user.name}</span>
                      <small>{user.email}</small>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="inbox-compose-field">
              <label>Subject</label>
              <input
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                placeholder="Subject"
              />
            </div>

            <div className="inbox-compose-field">
              <label>Message</label>
              <textarea
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                placeholder="Write your message..."
                rows={8}
              />
            </div>

            <div className="inbox-compose-field">
              <label>Attachments</label>
              <div className="inbox-compose-attach-bar">
                <button
                  type="button"
                  className="inbox-attach-trigger"
                  onClick={() => composeFileRef.current?.click()}
                >
                  +
                </button>
                <span className="inbox-attach-label">Add files</span>
              </div>
              <input
                ref={composeFileRef}
                type="file"
                multiple
                className="inbox-compose-file-input"
                onChange={(e) => {
                  const next = Array.from(e.target.files || []);
                  if (next.length === 0) return;
                  setComposeFiles((prev) => [...prev, ...next]);
                  e.target.value = '';
                }}
              />
              {composeFiles.length > 0 && (
                <ul className="inbox-compose-files">
                  {composeFiles.map((file, idx) => (
                    <li key={`${file.name}-${idx}`}>
                      <span>{file.name}</span>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setComposeFiles((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="inbox-compose-actions">
              <button className="btn btn-ghost" onClick={() => setComposeOpen(false)} disabled={composeSending}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleComposeSend} disabled={composeSending || resolvingRecipient || (composeResolution && composeResolution.canSend === false)}>
                {composeSending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        ) : !activeThread ? (
          <div className="inbox-empty-state">
            <span className="inbox-empty-icon">✉</span>
            <p>Select a thread to read messages</p>
          </div>
        ) : (
          <>
            {(() => {
              const headerUsers = getHeaderUsers(activeThread);
              return (
            <div className="inbox-msg-header">
              {isMobileViewport && (
                <button type="button" className="btn btn-ghost btn-sm inbox-mobile-back" onClick={handleBackToList}>
                  ← Inbox
                </button>
              )}
              <h2>{activeThread.subject}</h2>
              <span className="inbox-space-tag">{activeThread.spaceId?.name || (activeThread.type === 'dm' ? 'Direct Message' : 'Inbox')}</span>
              <button
                type="button"
                className="inbox-chain-delete-btn"
                onClick={handleDeleteConversation}
                title="Delete whole chain"
              >
                Delete Chain
              </button>
              {activeFolder !== 'archive' && activeFolder !== 'trash' && (
                <button
                  type="button"
                  className="inbox-chain-delete-btn"
                  onClick={handleArchiveConversation}
                  title="Archive this chain"
                >
                  Archive
                </button>
              )}
              {activeFolder === 'trash' && (
                <button
                  type="button"
                  className="inbox-chain-delete-btn"
                  onClick={handleRestoreConversation}
                  title="Restore this chain to Inbox"
                >
                  Restore
                </button>
              )}

              <div className="inbox-msg-addresses">
                {headerUsers.from && (
                  <div className="inbox-msg-address-row">
                    <span className="inbox-msg-address-label">From:</span>
                    <button
                      type="button"
                      className="inbox-msg-address-link"
                      onClick={() => startComposeToUser(headerUsers.from, activeThread?.subject)}
                    >
                      {headerUsers.from.name || 'Unknown'}
                    </button>
                  </div>
                )}
                <div className="inbox-msg-address-row">
                  <span className="inbox-msg-address-label">To:</span>
                  {headerUsers.to.length === 0 ? (
                    <span className="inbox-msg-address-muted">No recipients</span>
                  ) : (
                    <div className="inbox-msg-address-list">
                      {headerUsers.to.map((recipient) => (
                        <button
                          key={recipient._id}
                          type="button"
                          className="inbox-msg-address-link"
                          onClick={() => startComposeToUser(recipient, activeThread?.subject)}
                        >
                          {recipient.name || 'Unknown'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
              );
            })()}

            <div className="inbox-messages">
              {msgLoading ? (
                <p className="inbox-muted">Loading messages...</p>
              ) : messages.length === 0 ? (
                <p className="inbox-muted">No messages yet.</p>
              ) : (
                messages.map((m) => (
                  <div key={m._id} className="inbox-msg">
                    <div className="inbox-msg-avatar">
                      {m.sender?.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="inbox-msg-body">
                      <div className="inbox-msg-meta">
                        <span className="inbox-msg-sender">{m.sender?.name || 'Unknown'}</span>
                        <span className="inbox-msg-time">{timeAgo(m.createdAt)}</span>
                        {String(user?._id) === String(m.sender?._id) && (
                          <button
                            type="button"
                            className="inbox-msg-delete-btn"
                            onClick={() => handleDeleteMessage(m._id)}
                            title="Delete message"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      <p className="inbox-msg-text">
                        {m.body}
                      </p>
                      {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                        <div className="inbox-attachments">
                          {m.attachments.map((attachment, idx) => {
                            const fileId = attachment.fileRef?._id || attachment.fileRef;
                            const fileName = attachment.name || attachment.fileRef?.originalName || 'Attachment';
                            return (
                              <div className="inbox-attachment-item" key={fileId || `${m._id}-att-${idx}`}>
                                <button
                                  type="button"
                                  className="inbox-attachment-link"
                                  onClick={() => openFile(fileId).catch(() => alert('Unable to open attachment'))}
                                  title="Open in new tab"
                                >
                                  📎 {fileName}
                                </button>
                                <button
                                  type="button"
                                  className="inbox-attachment-download"
                                  onClick={() => downloadFile(fileId, fileName)}
                                  title="Download"
                                >
                                  ⬇
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <MessageCompose
              onSend={handleMessageSend}
              placeholder="Reply..."
            />
          </>
        )}
      </div>
    </div>
  );
}
